/**
 * Fee configuration modules using the createCrudApi factory.
 * feeSchedule, labTest, procedure, medicineItem, samples.
 */
import { fetchApi, API_BASE_URL } from '../client';
import { createCrudApi } from '../helpers';

// Strip sentinel UI values before sending to backend
const feeScheduleFilters = (f: any) => ({
  department: f?.department !== 'All Departments' ? f?.department : undefined,
  feeType: f?.feeType !== 'All Types' ? f?.feeType : undefined,
  status: f?.status !== 'All' ? f?.status : undefined,
  search: f?.search,
  page: f?.page,
  pageSize: f?.pageSize,
});

const labTestFilters = (f: any) => ({
  category: f?.category !== 'All Categories' ? f?.category : undefined,
  feeType: f?.feeType !== 'All Types' ? f?.feeType : undefined,
  status: f?.status !== 'All' ? f?.status : undefined,
  search: f?.search,
  page: f?.page,
  pageSize: f?.pageSize,
});

const procedureFilters = (f: any) => ({
  department: f?.department !== 'All Departments' ? f?.department : undefined,
  status: f?.status,
  search: f?.search,
  page: f?.page,
  pageSize: f?.pageSize,
});

const medicineFilters = (f: any) => ({
  category: f?.category !== 'All Categories' ? f?.category : undefined,
  status: f?.status,
  search: f?.search,
  page: f?.page,
  pageSize: f?.pageSize,
});

/** The clinic's free follow-up rule (medopd-docs 98). days null = off; fee null = free. */
export interface FollowUpPolicy {
  enabled: boolean;
  days: number | null;
  freeVisits: number;
  fee: number | null;
}

/** What one visit's consultation should cost under the follow-up rule. applies false = charge as usual. */
export interface ConsultationCharge {
  applies: boolean;
  kind: 'FREE' | 'REDUCED' | null;
  amount: number | null;
  paidVisitDate: string | null;
  validUntil: string | null;
  followUpNumber: number;
  allowed: number;
}

export const feeScheduleModule = {
  ...createCrudApi('/api/fee-schedules', feeScheduleFilters),
  /** With a doctorId the doctor's own consultation fee comes first (medopd-docs 136 §3 H2). */
  getDefault: (visitType: string, doctorId?: string) =>
    fetchApi<any>(`/api/fee-schedules/defaults?visitType=${encodeURIComponent(visitType)}${doctorId ? `&doctorId=${encodeURIComponent(doctorId)}` : ''}`),
  followUpPolicy: () => fetchApi<FollowUpPolicy>('/api/fee-schedules/follow-up-policy'),
  saveFollowUpPolicy: (p: { days: number | null; freeVisits: number; fee: number | null }) =>
    fetchApi<FollowUpPolicy>('/api/fee-schedules/follow-up-policy', { method: 'PUT', body: JSON.stringify(p) }),
  consultationCharge: (appointmentId: string) =>
    fetchApi<ConsultationCharge>(`/api/fee-schedules/consultation-charge?appointmentId=${encodeURIComponent(appointmentId)}`),
};

export const labTestModule = createCrudApi('/api/lab-tests', labTestFilters);

export const procedureModule = createCrudApi('/api/procedures', procedureFilters);

export const medicineItemModule = createCrudApi('/api/medicine-items', medicineFilters);

export const samplesModule = {
  downloadConsultationFees: () => { window.location.href = `${API_BASE_URL}/api/samples/consultation-fees`; },
  downloadLabTests: () => { window.location.href = `${API_BASE_URL}/api/samples/lab-tests`; },
  downloadMedicineItems: () => { window.location.href = `${API_BASE_URL}/api/samples/medicine-items`; },
  downloadProcedures: () => { window.location.href = `${API_BASE_URL}/api/samples/procedures`; },
};
